// Neon-pink blocky "S" over a radar + targeting reticle — matches the user's
// brand artwork. Pure SVG so it stays crisp at any DPI. The radar sweep is a
// CSS keyframe rotation; concentric rings sit behind the glowing glyph.
//
// Override with a raster copy of the exact artwork by dropping a PNG at
// `public/logo.png` — the component prefers that when present and falls
// back to the SVG otherwise.

import { useEffect, useState } from "react";

interface LogoProps {
  size?: number;
  className?: string;
  animated?: boolean;
  /**
   * Optional raster logo served from `public/`. When present (HEAD 200), this
   * file is rendered instead of the SVG. Default: `/logo.png`.
   */
  src?: string;
}

export function Logo({
  size = 36,
  className = "",
  animated = true,
  src = "/logo.png",
}: LogoProps) {
  // Prefer a raster asset when the user has dropped one in `public/`.
  const [hasRaster, setHasRaster] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(src, { method: "HEAD" })
      .then((r) => !cancelled && setHasRaster(r.ok))
      .catch(() => !cancelled && setHasRaster(false));
    return () => {
      cancelled = true;
    };
  }, [src]);

  if (hasRaster) {
    return (
      <img
        src={src}
        alt="SlabScanner"
        width={size}
        height={size}
        className={`${className} object-contain`}
        style={{ width: size, height: size }}
      />
    );
  }

  const rings = [0.96, 0.78, 0.6, 0.42, 0.24];
  const tickAngles = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];

  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="SlabScanner"
    >
      <defs>
        <radialGradient id="bg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#26051f" />
          <stop offset="80%" stopColor="#0d0511" />
          <stop offset="100%" stopColor="#050008" />
        </radialGradient>
        <filter id="neon" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.6" result="b1" />
          <feGaussianBlur stdDeviation="4" result="b2" />
          <feMerge>
            <feMergeNode in="b2" />
            <feMergeNode in="b1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="sweep" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ff2d92" stopOpacity="0" />
          <stop offset="70%" stopColor="#ff2d92" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#ff2d92" stopOpacity="0.9" />
        </linearGradient>
        <linearGradient id="sFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff5ab0" />
          <stop offset="100%" stopColor="#ff2d92" />
        </linearGradient>
      </defs>

      {/* Dark backdrop */}
      <circle cx="60" cy="60" r="58" fill="url(#bg)" />

      {/* Concentric radar rings */}
      {rings.map((r, i) => (
        <circle
          key={i}
          cx="60"
          cy="60"
          r={56 * r}
          fill="none"
          stroke="#ff2d92"
          strokeOpacity={0.18 + i * 0.06}
          strokeWidth="0.6"
        />
      ))}

      {/* Outer tick marks around the reticle */}
      <g stroke="#ff2d92" strokeOpacity="0.55" strokeWidth="0.8">
        {tickAngles.map((a) => {
          const rad = (a * Math.PI) / 180;
          const x1 = 60 + Math.cos(rad) * 57;
          const y1 = 60 + Math.sin(rad) * 57;
          const x2 = 60 + Math.cos(rad) * 52;
          const y2 = 60 + Math.sin(rad) * 52;
          return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
      </g>

      {/* Horizontal + vertical crosshairs */}
      <line x1="2" y1="60" x2="118" y2="60" stroke="#ff2d92" strokeOpacity="0.25" strokeWidth="0.5" />
      <line x1="60" y1="2" x2="60" y2="118" stroke="#ff2d92" strokeOpacity="0.25" strokeWidth="0.5" />

      {/* Sweeping radar arc — rotates around the centre */}
      <g
        style={{ transformOrigin: "60px 60px" }}
        className={animated ? "animate-radar" : ""}
      >
        <path d="M60 60 L118 60 A58 58 0 0 0 103 19 Z" fill="url(#sweep)" opacity="0.8" />
      </g>

      {/* Outer neon ring */}
      <circle
        cx="60"
        cy="60"
        r="56"
        fill="none"
        stroke="#ff2d92"
        strokeWidth="1.5"
        filter="url(#neon)"
      />

      {/*
        Blocky neon "S" — three horizontal bars joined by angled cuts, like a
        stylised parallelogram-Z. Drawn as a filled polygon so the weight
        matches the reference artwork.
      */}
      <g filter="url(#neon)">
        <polygon
          points="
            38,30
            92,30
            92,48
            58,48
            46,60
            82,60
            92,72
            92,90
            28,90
            28,72
            62,72
            74,60
            38,60
            28,48
          "
          fill="url(#sFill)"
          stroke="#ff8ac7"
          strokeWidth="1.2"
          strokeLinejoin="miter"
        />
      </g>

      {/* Centre dot */}
      <circle cx="60" cy="60" r="1.6" fill="#fff" filter="url(#neon)" />
    </svg>
  );
}
